import apiClient from './apiClient';

/**
 * Generate a unique order number
 * Format: GP-YYYYMMDD-XXXXX
 * @returns {string} Order number
 */
export const generateOrderNumber = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const random = String(Math.floor(Math.random() * 100000)).padStart(5, '0');
  return `GP-${year}${month}${day}-${random}`;
};

const mapOrder = (order) => {
  if (!order) return null;
  return {
    id: order.id,
    orderNumber: order.order_number,
    customerInfo: {
      fullName: order.shipping_address?.fullName || '',
      email: order.email,
      phone: order.phone || '',
      address: order.shipping_address?.address || '',
      city: order.shipping_address?.city || '',
      district: order.shipping_address?.district || '',
    },
    items: (order.items || []).map((item) => ({
      productId: item.product_id,
      name: item.name,
      price: item.unit_price,
      quantity: item.quantity,
      size: item.size || null,
      color: item.color || null,
      image: item.image || '',
    })),
    total: order.total_amount,
    subtotal: order.total_amount,
    shipping: 0,
    tax: 0,
    discount: 0,
    status: order.status,
    paymentMethod: order.payment_method,
    createdAt: order.created_at,
  };
};

/**
 * Create a new order
 * @param {Object} orderData - Order data
 * @param {Object} orderData.customerInfo - Customer information
 * @param {Array} orderData.items - Order items
 * @param {number} orderData.total - Total amount
 * @returns {Promise<Object>} Created order with order number
 */
export const createOrder = async (orderData) => {
  try {
    // Validate required fields
    if (!orderData.customerInfo || !orderData.items || orderData.items.length === 0) {
      throw new Error('Eksik sipariş bilgisi');
    }

    const payload = {
      email: orderData.customerInfo.email,
      phone: orderData.customerInfo.phone,
      items: orderData.items.map((item) => ({
        product_id: item.productId || null,
        sku: item.sku || null,
        name: item.name || '',
        quantity: typeof item.quantity === 'number' ? item.quantity : 1,
        unit_price: typeof item.price === 'number' ? item.price : 0,
        total_price:
          typeof item.price === 'number' && typeof item.quantity === 'number'
            ? item.price * item.quantity
            : 0,
        size: item.size || null,
        color: item.color || null,
        image: item.image || null,
      })),
      total_amount: typeof orderData.total === 'number' ? orderData.total : 0,
      payment_method: 'bank_transfer',
      shipping_address: {
        fullName: orderData.customerInfo.fullName || '',
        address: orderData.customerInfo.address || '',
        city: orderData.customerInfo.city || '',
        district: orderData.customerInfo.district || '',
      },
      billing_address: null,
    };

    const response = await apiClient.post('/orders', payload);
    return mapOrder(response.data);
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/**
 * Get order by order number
 * @param {string} orderNumber - Order number
 * @returns {Promise<Object|null>} Order object or null if not found
 */
export const getOrderByOrderNumber = async (orderNumber) => {
  try {
    const response = await apiClient.get('/orders/track', {
      params: { order_number: orderNumber },
    });
    return mapOrder(response.data);
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

/**
 * Get orders by customer email
 * @param {string} email - Customer email
 * @returns {Promise<Array>} Array of orders
 */
export const getOrdersByEmail = async (email) => {
  try {
    const response = await apiClient.get('/orders', { params: { email } });
    return response.data.map(mapOrder);
  } catch (error) {
    console.error('Error fetching orders by email:', error);
    throw error;
  }
};

/**
 * Get order by ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Order object
 */
export const getOrderById = async (orderId) => {
  try {
    const response = await apiClient.get(`/orders/${orderId}`);
    return mapOrder(response.data);
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};
